import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {ContentHeader, Loader, DatePicker} from '@components';
import {Button, Modal} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {logoutUser} from '@store/reducers/auth';
import ShowDailyLogTime from '@app/components/show-daily-log';
import {
  format,
  startOfYesterday,
  addDays,
  endOfDay,
  startOfDay
} from 'date-fns';
import Pagination from '../components/pagination/index';
import {DailiesAll, getTodayLogTime} from '../../src/services/daily';
import {getAllUserNames} from '../../src/services/auth';
import handleStatus from '../utils/ajax';

const ONE_DAY_MS = 86400000;

function ModalPopup({
  daily: {
    goals,
    status,
    created_At: createdAt,
    userId: {_id: id}
  },
  handleClose
}: any) {
  return (
    <Modal
      show
      size="xl"
      onHide={handleClose}
      centered
      dialogClassName="custom-modal-width"
    >
      <>
        <Modal.Header>
          <Modal.Title>Dailies</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{maxHeight: '400px', overflowY: 'auto'}}>
          <span>
            <b>Goals</b>
          </span>
          <div
            className="view-status"
            dangerouslySetInnerHTML={{__html: goals}}
          />
          <br />
          <span>
            <b>Status</b>
          </span>
          <div
            className="view-status"
            dangerouslySetInnerHTML={{__html: status}}
          />
          <ShowDailyLogTime userId={id} isAdmin createdAt={createdAt} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </>
    </Modal>
  );
}

const AllDailies = () => {
  const [goals, setGoals] = useState([]);
  const [selected, setSelected] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [daily, setDaily] = useState(null);
  const [startDate, setStartDate] = useState(0);
  const [endDate, setEndDate] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const datePickerRef = React.useRef<{resetDatePicker: () => void} | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [postPerPage] = useState(10);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getAll = async (query: any) => {
    setShowLoader(true);
    const resp = await DailiesAll(query);
    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);

    setShowLoader(false);
    if (resp.data.ok === true) {
      if (resp.data.data) {
        setGoals(resp.data.data);
        setTotalRecords(resp.data.totalRecords);
      }
    }
  };

  useEffect(() => {
    getAll({});
    getAllUsers();
  }, []);

  const isTodayDaily = (date: any) => {
    const today = new Date();
    const todayDate = format(today, 'yyyy-MM-dd');
    const dailyDate = format(date, 'yyyy-MM-dd');
    return todayDate === dailyDate;
  };

  const handleChange = (e: any) => {
    if (datePickerRef.current) {
      datePickerRef.current.resetDatePicker();
    }

    const value = e.target.value.toLowerCase();
    let newStartDate: number = 0;
    let newEndDate: number = 0;

    if (value === 'today') {
      newStartDate = startOfDay(new Date()).getTime();
      newEndDate = endOfDay(new Date()).getTime();
    } else if (value === 'yesterday') {
      const yesterday = new Date().getTime() - ONE_DAY_MS;
      newStartDate = startOfDay(new Date(yesterday)).getTime();
      newEndDate = endOfDay(new Date(yesterday)).getTime();
    }

    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setSelected(value);
  };

  const handleCustomDateChange = ({startDate, endDate}: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const filter = () => {
    getAll({startDate, endDate, userId: user});
  };

  const handleReset = () => {
    if (datePickerRef.current) {
      datePickerRef.current.resetDatePicker();
    }
  };

  const getAllUsers = async () => {
    const resp = await getAllUserNames();
    handleStatus(resp.status, () => dispatch(logoutUser()), navigate);
    setUsers(resp.data.data);
  };

  const onChange = (e: any) => {
    let value = e?.target?.value;
    if (value === 'Name') {
      value = null;
    }
    setUser(value);
  };

  const resetAllDailies = () => {
    handleReset();
    setStartDate(0);
    setEndDate(0);
    setSelected('all');
    setUser('');
    getAll({});
  };

  const handlePopup = (daily: any) => {
    setShowPopup(true);
    setDaily(daily);
  };

  const handleClose = () => {
    setShowPopup(false);
    setDaily(null);
  };

  const paginate = (pageno: number) => {
    getAll({
      startDate,
      endDate,
      userId: user,
      pageNo: pageno
    });
    setCurrentPage(pageno);
  };

  return (
    <>
      <div>
        <ContentHeader title="All Employees Dailies" path="" />
        {showPopup && <ModalPopup handleClose={handleClose} daily={daily} />}
        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">All Dailies</h3>
              </div>
              <div className="card-body">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header d-flex justify-content-end flex-wrap">
                      {/* <h3 className="m-0">All Dailies</h3> */}

                      <div className="col-md-3">
                        <select
                          onChange={onChange}
                          value={user}
                          className="form-control"
                        >
                          <option selected>Name</option>
                          {users.map(({_id: id, name}: any) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-3">
                        <DatePicker
                          ref={datePickerRef}
                          onSelect={handleCustomDateChange}
                        />
                      </div>

                      <div className="col-md-2">
                        <select
                          className="form-control"
                          value={selected}
                          onChange={handleChange}
                        >
                          <option value="all">All</option>
                          <option value="today">Today</option>
                          <option value="yesterday">Yesterday</option>
                        </select>
                      </div>

                      <div className="col-md-1">
                        <Button onClick={filter} variant="primary">
                          Search
                        </Button>
                      </div>

                      <div className="col-md-1">
                        <Button onClick={resetAllDailies} variant="primary">
                          Reset
                        </Button>
                      </div>
                    </div>

                    <div className="card-body table-responsive p-0">
                      {showLoader ? (
                        <Loader title="My Dailies" />
                      ) : (
                        <table className="table table-hover text-nowrap">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th style={{textAlign: 'center'}}>Date</th>
                              <th style={{textAlign: 'center'}}>Start Time</th>
                              <th style={{textAlign: 'center'}}>Break Time</th>
                              <th style={{textAlign: 'center'}}>End Time</th>
                              <th style={{textAlign: 'center'}}>Total Hours</th>
                              <th style={{textAlign: 'right'}}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {goals.map((daily, index) => {
                             
                              const {
                                created_At: createdAt,
                                status,
                                breakTime,
                                updated_At: updatedAt,
                                totalHours,
                                userId: {name}
                              } = daily;

                              function getRemainingAfterBreak(t1: any, t2: any, breakTime:string) {
                                const diffSeconds = Math.abs(t1 - t2) / 1000;
                                const breakSeconds = breakTimeToMinutes(breakTime) * 60;
                                const remainingSeconds = diffSeconds - breakSeconds; 
                              
                                const workTime = 7 * 60 * 60 + ( 45 * 60 ); 
                                const isGood = workTime - remainingSeconds < 0 ? true : false;
                                return isGood;  
                              }

                              function showRedBg(createdAt:number, updatedAt:number, breakTime:string) {
                                const isStatusPending = (!isTodayDaily(createdAt) && !status);

                                if(isStatusPending) {
                                  return "daily-missed";
                                }

                                if(!status){
                                  return 'user-daily';
                                }

                                const remainingAfterBreak = !getRemainingAfterBreak(createdAt, updatedAt, breakTime) ? "daily-flagged" : "user-daily";
                                return remainingAfterBreak;
                           
                              }

                              function breakTimeToMinutes(breakTimeString:string = '0h45m') {
                                if(!breakTimeString) return 0;

                                const hoursMatch = breakTimeString.match(/(\d+)h/);
                                const minutesMatch = breakTimeString.match(/(\d+)m/);
                              
                                const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
                                const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
                              
                                return hours * 60 + minutes;
                              }

                              
                              return (
                                <tr
                                  className={
                                    
                                    showRedBg(createdAt, updatedAt, breakTime)
                                  }
                                >
                                  <td>{name}</td>
                                  <td style={{textAlign: 'center'}}>
                                    {format(new Date(createdAt), 'dd/MM/yyyy')}
                                  </td>
                                  <td style={{textAlign: 'center'}}>
                                    {format(new Date(createdAt), 'h:mm a')}
                                  </td>
                                  <td style={{textAlign: 'center'}}>
                                    {breakTime || 'N/A'}
                                  </td>
                                  <td style={{textAlign: 'center'}}>
                                    {format(new Date(updatedAt), 'h:mm a')}
                                  </td>
                                  <td style={{textAlign: 'center'}}>
                                    {totalHours || 'N/A'}
                                  </td>
                                  <td style={{textAlign: 'right'}}>
                                    <Button
                                      variant="primary"
                                      onClick={() => handlePopup(daily)}
                                    >
                                      View
                                    </Button>{' '}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Pagination
        postsPerPage={postPerPage}
        totalPosts={totalRecords}
        paginate={paginate}
        currentPage={currentPage}
      />
    </>
  );
};

export default AllDailies;
